import React, { useState, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

export default function IngredientAutocomplete({ 
    value, 
    onChange, 
    placeholder = "Start typing ingredient name...",
    style = {},
    className = ""
}) {
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
    const [isSelectingManually, setIsSelectingManually] = useState(false);
    const inputRef = useRef(null);

    const handleInputChange = async (newValue) => {
        onChange(newValue);
        
        // Search for suggestions if value length >= 2
        if (newValue.length >= 2) {
            try {
                const response = await axios.get(`${API_BASE_URL}/cupboard/search/ingredients`, {
                    params: { q: newValue, limit: 10 }
                });
                
                if (response.data.success) {
                    setSuggestions(response.data.ingredients);
                    setShowSuggestions(true);
                    setActiveSuggestionIndex(-1);
                }
            } catch (error) {
                console.error('Error searching ingredients:', error);
                setSuggestions([]);
                setShowSuggestions(false);
            }
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setIsSelectingManually(true);
        onChange(suggestion.name);
        setShowSuggestions(false);
        setSuggestions([]);
        
        // Reset the manual selection flag after a short delay
        setTimeout(() => {
            setIsSelectingManually(false);
        }, 100);
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions || suggestions.length === 0) return;
        
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const nextIndex = activeSuggestionIndex < suggestions.length - 1 ? activeSuggestionIndex + 1 : 0;
            setActiveSuggestionIndex(nextIndex);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const nextIndex = activeSuggestionIndex > 0 ? activeSuggestionIndex - 1 : suggestions.length - 1;
            setActiveSuggestionIndex(nextIndex);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (activeSuggestionIndex >= 0 && suggestions[activeSuggestionIndex]) {
                handleSuggestionClick(suggestions[activeSuggestionIndex]);
            }
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
        }
    };

    const handleBlur = () => {
        // If user is manually selecting, don't do anything
        if (isSelectingManually) {
            return;
        }

        // Delay to allow clicking on suggestions
        setTimeout(() => {
            if (isSelectingManually) {
                return;
            }

            if (showSuggestions && suggestions.length > 0) {
                // Check if current input exactly matches a suggestion
                const exactMatch = suggestions.find(
                    s => s.name.toLowerCase() === value.toLowerCase()
                );
                
                if (exactMatch) {
                    onChange(exactMatch.name);
                } else if (value.length >= 2) {
                    // Auto-select best match
                    const bestMatch = suggestions[0];
                    if (bestMatch) {
                        onChange(bestMatch.name);
                    }
                }
            }
            
            setShowSuggestions(false);
        }, 300);
    };

    const handleFocus = () => {
        if (value && value.length >= 2 && suggestions.length > 0) {
            setShowSuggestions(true);
        }
    };

    return (
        <div style={{ position: 'relative', ...style }} className={className}>
            <input
                ref={inputRef}
                type="text"
                placeholder={placeholder}
                value={value || ''}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
                autoComplete="off"
                style={{ 
                    width: '100%',
                    borderColor: showSuggestions ? '#007bff' : '#ddd'
                }}
            />
            {showSuggestions && suggestions.length > 0 && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderTop: 'none',
                    borderRadius: '0 0 4px 4px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                    <div style={{
                        padding: '8px 12px',
                        backgroundColor: '#f8f9fa',
                        borderBottom: '1px solid #eee',
                        fontSize: '12px',
                        color: '#666'
                    }}>
                        {suggestions.length} ingredient{suggestions.length !== 1 ? 's' : ''} found - Click to select
                    </div>
                    {suggestions.map((suggestion, suggestionIndex) => (
                        <div
                            key={suggestionIndex}
                            className={activeSuggestionIndex === suggestionIndex ? 'active' : ''}
                            onMouseDown={() => {
                                setIsSelectingManually(true);
                                handleSuggestionClick(suggestion);
                            }}
                            onMouseEnter={() => setActiveSuggestionIndex(suggestionIndex)}
                            style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                backgroundColor: activeSuggestionIndex === suggestionIndex ? '#e9ecef' : 'white',
                                borderBottom: suggestionIndex < suggestions.length - 1 ? '1px solid #eee' : 'none'
                            }}
                        >
                            {suggestion.name}
                            {suggestionIndex === 0 && (
                                <span style={{ 
                                    fontSize: '10px', 
                                    color: '#007bff', 
                                    marginLeft: '8px' 
                                }}>
                                    (Best match)
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

